"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { UtensilsCrossed, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface TechHighlight {
  label: Record<string, string>;
  val: string;
  desc: Record<string, string>;
}
const technicalHighlights: TechHighlight[] = [{
  label: {
    en: "Alloy Grades",
    zh: "合金牌号",
    es: "Grados de aleación",
    de: "Legierungsgüten",
    fr: "Nuances d'alliages",
    pt: "Graus de liga"
  },
  val: "AISI 316L / 304L",
  desc: {
    en: "Cleanable corrosion-resistant grades",
    zh: "易洁抗腐蚀卫生合金",
    es: "Aleaciones limpiables resistentes a la corrosión",
    de: "Reinigbare korrosionsbeständige Güten",
    fr: "Nuances nettoyables résistantes à la corrosion",
    pt: "Ligas limpáveis resistentes à corrosão"
  }
}, {
  label: {
    en: "Surface Finish",
    zh: "表面光洁度",
    es: "Acabado superficial",
    de: "Oberflächengüte",
    fr: "Finition de surface",
    pt: "Acabamento superficial"
  },
  val: "Ra 0.4–0.8 µm",
  desc: {
    en: "Ultrasonic cleaned & passivated",
    zh: "超声波除油与化学钝化",
    es: "Limpieza ultrasónica y pasivado",
    de: "Ultraschallgereinigt & passiviert",
    fr: "Nettoyage aux ultrasons et passivation",
    pt: "Limpeza ultrassônica e passivação"
  }
}, {
  label: {
    en: "Cleaning Protocol",
    zh: "清洗兼容性",
    es: "Protocolo de limpieza",
    de: "Reinigungsprotokoll",
    fr: "Protocole de nettoyage",
    pt: "Protocolo de limpeza"
  },
  val: "CIP & SIP Rated",
  desc: {
    en: "Steam & sanitary rinse stable",
    zh: "耐高温原位清洗与灭菌",
    es: "Resistente a vapor y enjuague sanitario",
    de: "Dampf- & sanitärspülbeständig",
    fr: "Résistant à la vapeur et aux rinçages sanitaires",
    pt: "Estável a vapor e enxágues sanitários"
  }
}, {
  label: {
    en: "Filtration Rating",
    zh: "过滤精度",
    es: "Grado de filtración",
    de: "Filterfeinheit",
    fr: "Seuil de filtration",
    pt: "Grau de filtração"
  },
  val: "1–500 µm",
  desc: {
    en: "Calibrated zero-shedding mesh",
    zh: "校准无微粒脱落织网",
    es: "Malla calibrada sin desprendimiento",
    de: "Kalibriertes partikelfreies Gewebe",
    fr: "Toile calibrée sans relargage",
    pt: "Malha calibrada sem desprendimento"
  }
}];
const copy = {
  eyebrowCategory: {
    en: "Food & Pharmaceutical Filtration",
    zh: "食品与医药过滤",
    es: "Filtración alimentaria y farmacéutica",
    de: "Lebensmittel- und Pharmafiltration",
    fr: "Filtration agroalimentaire et pharmaceutique",
    pt: "Filtração de alimentos e farmacêutica"
  },
  h1: {
    en: "Hygienic Stainless Steel Mesh for Food & Pharmaceutical Processing",
    zh: "高纯度卫生级不锈钢丝网，专为食品与制药工艺定制",
    es: "Malla higiénica de acero inoxidable para procesamiento alimentario y farmacéutico",
    de: "Hygienisches Edelstahl-Drahtgewebe für Lebensmittel- und Pharmaprozesse",
    fr: "Toile hygiénique en acier inoxydable pour procédés agroalimentaires et pharmaceutiques",
    pt: "Malha higiênica de aço inoxidável para processamento de alimentos e farmacêutico"
  },
  lead: {
    en: "Engineered from AISI 316L and 304L stainless steel alloys with optional ultrasonic cleaning and electropolished passivation, ensuring zero-contaminant fluid straining and powder classification across sanitary food, beverage, and active pharmaceutical ingredient lines.",
    zh: "选用 AISI 316L 与 304L 奥氏体不锈钢制造，经超声波脱脂与表面电抛光钝化处理，为食品加工、饮料灌装、活性药物原料（API）分离及生物制药提供无污染、高精度的流体与粉体筛分保障。",
    es: "Fabricada en aleaciones de acero inoxidable AISI 316L y 304L con desengrasado ultrasónico y pasivado electropulido opcionales, garantizando retención de fluidos y clasificación de polvo sin contaminantes en líneas de alimentos, bebidas y principios activos farmacéuticos.",
    de: "Gefertigt aus Edelstahllegierungen AISI 316L und 304L mit optionaler Ultraschallreinigung und elektropolierter Passivierung für kontaminationsfreies Filtrieren von Flüssigkeiten und Pulverklassierung in Lebensmittel-, Getränke- und pharmazeutischen Wirkstoffanlagen.",
    fr: "Conçue à partir d'alliages d'acier inoxydable AISI 316L et 304L avec dégraissage aux ultrasons et passivation électropolie en option, garantissant une filtration des fluides et une classification des poudres sans contaminant pour l'agroalimentaire, les boissons et les principes actifs pharmaceutiques.",
    pt: "Fabricada com ligas de aço inoxidável AISI 316L e 304L com limpeza ultrassônica opcional e passivação eletropolida, garantindo retenção de fluidos e classificação de pó livres de contaminação em linhas de alimentos, bebidas e ingredientes farmacêuticos ativos."
  },
  ctaQuote: {
    en: "Request Technical Quote",
    zh: "索取技术报价",
    es: "Solicitar cotización técnica",
    de: "Technisches Angebot anfordern",
    fr: "Demander un devis technique",
    pt: "Solicitar cotação técnica"
  },
  ctaMaterials: {
    en: "Filter Mesh Materials",
    zh: "滤网材质指南",
    es: "Materiales de malla filtrante",
    de: "Filtergewebe-Werkstoffe",
    fr: "Matériaux de toile filtrante",
    pt: "Materiais de malha filtrante"
  },
  imgAlt: {
    en: "High-precision hygienic stainless steel wire mesh and filter disc components for food and pharmaceutical processing",
    zh: "用于食品与制药工艺的高精度卫生级不锈钢过滤丝网与滤片组件",
    es: "Malla de alambre de acero inoxidable higiénica de alta precisión y discos filtrantes para procesos alimentarios y farmacéuticos",
    de: "Hochpräzises hygienisches Edelstahl-Drahtgewebe und Filterscheiben-Komponenten für Lebensmittel- und Pharmaprozesse",
    fr: "Toile métallique hygiénique en acier inoxydable haute précision et disques filtrants pour procédés agroalimentaires et pharmaceutiques",
    pt: "Malha de arame de aço inoxidável higiênica de alta precisão e discos filtrantes para processamento de alimentos e farmacêutico"
  },
  cardBadge: {
    en: "Hygienic Design Standard",
    zh: "卫生级制造规范",
    es: "Norma de diseño higiénico",
    de: "Hygienic-Design-Standard",
    fr: "Standard de conception hygiénique",
    pt: "Padrão de projeto higiênico"
  },
  cardTitle: {
    en: "AISI 316L Hygienic Filter Element",
    zh: "AISI 316L 卫生级过滤元件",
    es: "Elemento filtrante higiénico AISI 316L",
    de: "Hygienisches Filterelement aus AISI 316L",
    fr: "Élément filtrant hygiénique AISI 316L",
    pt: "Elemento filtrante higiênico AISI 316L"
  },
  cardSubtitle: {
    en: "High-integrity mesh assembly for sanitary liquids, purified water, and sterile pharma filtration",
    zh: "适用于食品接触流体、纯水管路与无菌药液过滤的高完整性滤网组件",
    es: "Conjunto de malla de alta integridad para líquidos sanitarios, agua purificada y filtración farmacéutica estéril",
    de: "Hochintegrierte Gewebekomponente für hygienische Flüssigkeiten, Reinstwasser und sterile Pharmafiltration",
    fr: "Assemblage filtrant haute intégrité pour fluides sanitaires, eau purifiée et filtration pharmaceutique stérile",
    pt: "Montagem de malha de alta integridade para líquidos sanitários, água purificada e filtração farmacêutica estéril"
  },
  cardFooterLeft: {
    en: "Discs, cylinders, baskets & OEM packs",
    zh: "滤片、滤筒、筛篮与定制组件",
    es: "Discos, cilindros, cestas y conjuntos OEM",
    de: "Scheiben, Zylinder, Körbe & OEM-Pakete",
    fr: "Disques, cylindres, paniers et packs OEM",
    pt: "Discos, cilindros, cestos e pacotes OEM"
  },
  cardFooterRight: {
    en: "OEM Custom",
    zh: "OEM定制",
    es: "OEM personalizado",
    de: "OEM-Kundenspezifisch",
    fr: "OEM sur mesure",
    pt: "OEM personalizado"
  }
};
export default function FoodPharmaHero() {
  const {
    lang
  } = useLanguage();
  return <section id="foodpharmahero" data-nav="light" className="relative bg-background text-foreground mf-hero border-b border-border overflow-hidden">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Eyebrow Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 mf-caption font-mono tracking-tight text-foreground">
                <UtensilsCrossed className="size-3.5 text-primary" />
                <span>{pick(lang, copy.eyebrowCategory)}</span>
              </Badge>
              <Badge variant="outline" className="rounded-md border border-border bg-muted text-foreground px-2.5 py-1 mf-caption font-mono">
                <ShieldCheck className="mr-1 size-3 text-primary inline" />
                ISO 9044 / ASTM E2016
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, copy.h1)}
            </h1>

            {/* Supporting Paragraph */}
            <p className="mt-4 max-w-2xl mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>

            {/* 2 CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, copy.ctaQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/StainlessSteelFilterMeshMaterialsPage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, copy.ctaMaterials)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Visual Spec Panel (5 cols on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media relative mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png" alt={pick(lang, copy.imgAlt)} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-card text-foreground border border-border px-2.5 py-1 mf-caption font-semibold rounded-md flex items-center gap-1.5">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>{pick(lang, copy.cardBadge)}</span>
                  </Badge>
                </div>
              </div>

              {/* Technical Specifications Grid & Details */}
              <CardContent className="p-4 bg-card">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>{pick(lang, copy.cardFooterLeft)}</span>
                  </div>
                  <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, copy.cardFooterRight)}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Technical Specs Strip */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value">
                {spec.val}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}